/**
 * Accountant-format statement table shared by the Balance Sheet and the Cash Flow Statement.
 * Rows: { kind: 'section' | 'line' | 'subtotal' | 'total' | 'note', label, values: [n, …], accounts }
 * Negatives are shown in brackets, totals are ruled, and a line with accounts expands to show
 * them; each account's magnifier opens its journal entries (onDrill) without leaving the page.
 */
import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Typography,
} from '@mui/material';
import { ExpandMore, ChevronRight, ManageSearch as DrillIcon } from '@mui/icons-material';

export const fmtAcct = (n) => {
  if (n === null || n === undefined) return '';
  const v = Math.round(Number(n) * 100) / 100;
  if (Math.abs(v) < 0.005) return '–';
  const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};

const numCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

function LineRow({ row, columns, onDrill }) {
  const [open, setOpen] = useState(false);
  const accounts = row.accounts || [];
  const canOpen = accounts.length > 0;
  return (
    <>
      <TableRow hover={canOpen} onClick={canOpen ? () => setOpen(!open) : undefined} sx={{ cursor: canOpen ? 'pointer' : 'default' }}>
        <TableCell sx={{ pl: 3 }}>
          {canOpen
            ? (open ? <ExpandMore fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: 'text.secondary' }} />
                    : <ChevronRight fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5, color: 'text.secondary' }} />)
            : <span style={{ display: 'inline-block', width: 24 }} />}
          {row.label}
          {row.hint && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{row.hint}</Typography>}
        </TableCell>
        {columns.map((c, i) => <TableCell key={c} align="right" sx={numCell}>{fmtAcct(row.values[i])}</TableCell>)}
        <TableCell />
      </TableRow>
      {open && accounts.map((a) => (
        <TableRow key={a.code} sx={{ bgcolor: 'grey.50' }}>
          <TableCell sx={{ pl: 8, fontSize: '0.8rem', color: 'text.secondary' }}>{a.code} — {a.name}</TableCell>
          {columns.map((c, i) => (
            <TableCell key={c} align="right" sx={{ ...numCell, fontSize: '0.8rem', color: 'text.secondary' }}>
              {a.values[i] === undefined ? '' : fmtAcct(a.values[i])}
            </TableCell>
          ))}
          <TableCell align="center" sx={{ width: 48 }}>
            {onDrill && a.code && (
              <Tooltip title={`Journal entries for ${a.code}`}>
                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onDrill(a.code, a.name); }}>
                  <DrillIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default function StatementTable({ columns, rows, onDrill }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>USD</TableCell>
            {columns.map(c => <TableCell key={c} align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{c}</TableCell>)}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            if (row.kind === 'section') {
              return (
                <TableRow key={idx}>
                  <TableCell colSpan={columns.length + 2} sx={{ fontWeight: 700, pt: 3, borderBottom: 'none' }}>{row.label}</TableCell>
                </TableRow>
              );
            }
            if (row.kind === 'note') {
              return (
                <TableRow key={idx}>
                  <TableCell colSpan={columns.length + 2} sx={{ fontSize: '0.75rem', color: 'text.secondary', borderBottom: 'none', pl: 3 }}>{row.label}</TableCell>
                </TableRow>
              );
            }
            if (row.kind === 'subtotal' || row.kind === 'total') {
              const total = row.kind === 'total';
              return (
                <TableRow key={idx}>
                  <TableCell sx={{ fontWeight: 700, pl: total ? 2 : 3 }}>{row.label}</TableCell>
                  {columns.map((c, i) => (
                    <TableCell key={c} align="right" sx={{
                      ...numCell, fontWeight: 700,
                      borderTop: '1px solid', borderTopColor: 'text.primary',
                      borderBottom: total ? '3px double' : undefined, borderBottomColor: total ? 'text.primary' : undefined,
                    }}>
                      {fmtAcct(row.values[i])}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              );
            }
            return <LineRow key={idx} row={row} columns={columns} onDrill={onDrill} />;
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
